// apps/web/src/pages/spread/RecipeOptionGroup.tsx
//
// Phase 9 Plan 13 Task 1 — RecipeOptionGroup with disabled-option tooltip
// overlay (REND-05 / CSTR-04 / SPRD-03).
//
// Phase 11.7 Plan 02 Task 1 — Surface 1 made BOTH group-column-grouped AND
// interactive (selectable), via path (a): RecipeOptionGroup now reads + writes
// RecipeStateContext directly and renders the non-violated options as
// selectable Pills wired to the shared `toggle` helper (Plan 01). The
// option-set stays OUT of the TemplateRenderer loop (W2 single-render rule),
// so RecipeRenderEngine Test 7 (cardinality === 1) still holds — the engine is
// NOT edited; selection-awareness lives here.
//
// Composes the cross-system functionality of:
//   - vocabulary substitution (Plan 09-04 substituteExplanation + useTerm) and
//   - constraint evaluation (Plan 09-03/06 EvaluationResult.verdicts) and
//   - recipe-state selection (Phase 11.3 RecipeStateContext + 11.3 persistence)
// into a single rendered surface: option-set widgets whose options are
// selectable Pills bucketed into group columns, each option carrying a
// hover tooltip when a constraint blocks the widget. This is the cross-system
// fixture 8 functionality (D-18) — the constraint AST's `explanation` field is
// the source of truth, vocabulary tokens are substituted at render time, and
// the result reaches the user via a Radix Tooltip.
//
// Per UI-SPEC §"Disabled option tooltip" lines 398-402:
//   - Disabled options use aria-disabled="true" (NOT the `disabled` attribute)
//     so they remain keyboard-focusable for screen readers.
//   - Tooltip body is the LIVE substituted `explanation` text from the AST.
//   - Tooltip header label is `{kind.upper} RULE` (e.g. "CARDINALITY RULE")
//     in --recipe-accent color above the body.
//
// A11y (UI-SPEC §Accessibility item 1, the 11.5 BLOCKER lesson): the enabled
// options are the Pill primitive — native <button role="checkbox"> + aria-checked
// — INDEPENDENTLY TABBABLE (native Tab order + Space/Enter activation). No roving
// tabindex is added (the floor avoids the focus-strand class of bug entirely);
// the browser test asserts document.activeElement after Tab. Constraint-blocked
// options stay aria-disabled (focusable, tooltip via aria-describedby); at-max
// options use the Pill `disabled` (inert) — at-max never fires for the canonical
// {min:0, no-max} fixture but is wired for max-bearing recipes.
//
// SECURITY (T-09-13-03 / T-11.7-04 mitigation, see plan threat model):
//   substituteExplanation is a pure string-replace operation (regex). The
//   substituted string flows through React JSX text-escaping — NEVER through
//   dangerouslySetInnerHTML. All option labels + group-column headers are JSX
//   text children (React auto-escapes). The tooltip's surface uses recipe-themed
//   CSS variables (--recipe-card / --recipe-ink / --recipe-line / --recipe-accent)
//   per UI-SPEC §B color contract.
//
// CONTEXT:
//   - recipe.widgets[] is iterated; only `option-set` templates produce
//     option pills. Other templates render nothing here (the engine's
//     TemplateRenderer chain mounts those).
//   - evalResult.verdicts is consulted per widget instance: any verdict with
//     status === "violated" whose constraint targets this widget triggers
//     the tooltip overlay on every option in that widget. The v1 surface
//     is the simplest correct behavior — Phase 13+ may refine the targeting
//     down to per-option once selectability constraints carry option ids.
//   - grouping reads the option's `groupId` DATA value (Plan 01 schema field),
//     NEVER a literal grouping identifier — the C1 grep gate
//     (scripts/grep-no-recipe-identifiers.sh) forbids hardcoded grouping
//     vocabulary in engine source. Options whose groupId is unset land in a
//     trailing column with an empty header (graceful degrade).
//
// AUTHR-06 hygiene: NO useWatch, NO form-state subscription — pure props in,
// pure JSX out (plus the RecipeStateContext read, which is a Context read, not a
// form subscription). The parent threads the EvaluationResult down. Mission
// Critical perf budget preserved (Phase 8 AUTHR-06 13.73ms p99 amortized).

import type {
  ConstraintVerdict,
  EvaluationResult,
} from "@the-recipe-spread/shared";
import { substituteExplanation } from "@the-recipe-spread/shared";
import { useContext } from "react";
import { Pill } from "@/components/ui/pill";
import { useTerm } from "@/lib/vocabulary/useTerm";
import { RecipeStateContext } from "@/widgets/recipe-state-provider";
// Phase 11.7 Plan 02 — bucket options by groupId and resolve each column header
// from the recipe's optionCategories. resolveGroupLabel is the SHARED resolver
// lifted by Plan 04 into summary-checklist/group-label.ts; Surface 1 (here) and
// Surface 3 (the checklist) call ONE implementation — do NOT re-define it.
import { resolveGroupLabel } from "@/widgets/summary-checklist/group-label";
// Phase 11.7 Plan 02 — selection toggle is the SHARED pure helper lifted by
// Plan 01 into option-set/toggle.ts (the cardinality guards live in ONE place;
// OptionSetRenderer uses the same helper). Do NOT re-implement the toggle.
import { toggle } from "@/widgets/option-set/toggle";
// Phase 11.7 D2 LIFT — DisabledOptionWithTooltip + firstViolatedFor were moved
// to the shared constraint-pill module so Plan 02 (Surface 1) and Plan 03
// (Surface 2 Duplicate pill) import ONE implementation. This file imports them
// back; the Tooltip primitives are no longer used directly here (the lifted
// component owns them).
import {
  DisabledOptionWithTooltip,
  firstViolatedFor,
} from "./constraint-pill";

// ---------------------------------------------------------------------------
// Local Recipe + Widget shapes. The RecipeOptionGroup consumes a STRUCTURAL
// subset of the full RecipeDefinitionV1 shape — only the fields it actually
// reads. This avoids dragging the entire recipe-v1 schema's type surface into
// the prop type at the cost of a small duplicated shape definition.
// ---------------------------------------------------------------------------

export interface RecipeOptionShape {
  id: string;
  label: string;
  // Phase 11.7 Plan 02 — grouping DATA value (Plan 01 optional schema field).
  // Options sharing a groupId render under one column; absent → trailing
  // ungrouped column with an empty header. Read as DATA; never a literal.
  groupId?: string | undefined;
}

export interface RecipeOptionSetConfigShape {
  cardinality: { min?: number | undefined; max?: number | undefined };
  options: ReadonlyArray<RecipeOptionShape>;
}

export interface RecipeWidgetShape {
  instanceId: string;
  templateId: string;
  name?: string | undefined;
  config: unknown;
}

// Phase 11.7 Plan 02 — the column-header resolver reads OptionCategory {id,label}
// entries. Structural subset (mirrors group-label.ts GroupLabelSource) so the
// prop type does not import the full Zod-derived OptionCategory surface. The
// RecipeRenderEngine adapter does NOT thread this through (the engine is
// confirmed-preserve / unedited), so it is `undefined` on the live spread render
// → resolveGroupLabel falls back to the raw id (or empty for _ungrouped). A
// caller that supplies it (tests, or a future engine pass) gets resolved labels.
export type RecipeOptionCategoryShape = ReadonlyArray<{ id: string; label: string }>;

export interface RecipeShape {
  id: string;
  widgets: ReadonlyArray<RecipeWidgetShape>;
  optionCategories?: RecipeOptionCategoryShape | undefined;
}

export interface RecipeOptionGroupProps {
  recipe: RecipeShape;
  evalResult: EvaluationResult;
}

// ---------------------------------------------------------------------------
// Helpers — pure functions over the EvaluationResult.
// firstViolatedFor lives in ./constraint-pill (Phase 11.7 D2 LIFT) and is
// imported above; asOptionSetConfig stays local to this overlay.
// ---------------------------------------------------------------------------

// asOptionSetConfig — narrow the unknown `config` to the option-set shape.
// Returns undefined if the config doesn't structurally match — the widget is
// skipped silently (the TemplateRenderer chain renders other templates; we
// don't double-render option-sets here).
function asOptionSetConfig(config: unknown): RecipeOptionSetConfigShape | undefined {
  if (typeof config !== "object" || config === null) return undefined;
  const c = config as { options?: unknown; cardinality?: unknown };
  if (!Array.isArray(c.options)) return undefined;
  // Each option must structurally have id + label; groupId is optional DATA.
  const options: RecipeOptionShape[] = [];
  for (const opt of c.options) {
    if (typeof opt !== "object" || opt === null) return undefined;
    const o = opt as { id?: unknown; label?: unknown; groupId?: unknown };
    if (typeof o.id !== "string" || typeof o.label !== "string") return undefined;
    options.push({
      id: o.id,
      label: o.label,
      // Carry the grouping field through the narrowing (Plan 02). Only a
      // non-empty string is a real bucket key; anything else → ungrouped.
      groupId: typeof o.groupId === "string" ? o.groupId : undefined,
    });
  }
  // cardinality is required by the option-set schema; tolerate missing
  // structurally. min/max drive the shared toggle's cardinality guards.
  return {
    cardinality:
      typeof c.cardinality === "object" && c.cardinality !== null
        ? (c.cardinality as { min?: number; max?: number })
        : {},
    options,
  };
}

// ---------------------------------------------------------------------------
// RecipeOptionGroup — top-level component.
// ---------------------------------------------------------------------------

export function RecipeOptionGroup({
  recipe,
  evalResult,
}: RecipeOptionGroupProps): React.ReactElement {
  // useTerm hook — module-level binding so substituteExplanation gets a
  // stable read function. Used by EACH widget's substituted tooltip body.
  // useTerm itself is a Context read; no form subscription, no useWatch.
  const readVocab = useTermReader();

  // Phase 11.7 Plan 02 — the recipe-state context. The engine already mounts
  // inside <RecipeStateProvider> (RecipeRenderEngine.tsx:73), so the context is
  // available; this component NEVER self-wraps a provider (documented
  // anti-pattern — it would defeat per-caller state scoping). May be null in a
  // bare render (e.g. a unit test with no provider) — fall back to no-selection
  // + no-op write so the surface still renders.
  const stateCtx = useContext(RecipeStateContext);

  return (
    <div
      className="flex flex-col gap-4"
      data-testid="recipe-option-group"
      data-recipe-id={recipe.id}
    >
      {recipe.widgets.map((widget) => {
        if (widget.templateId !== "option-set") return null;
        const config = asOptionSetConfig(widget.config);
        if (config === undefined) return null;
        const violated = firstViolatedFor(widget.instanceId, evalResult.verdicts);
        // Per-widget selection read + write (mirrors template-renderer.tsx:98-101
        // — the only place that wires state→context today). The {selected}
        // writes flow through the 11.3 PersistenceStrategy debounced save
        // automatically; no new persistence work here.
        const state = stateCtx?.states.get(widget.instanceId);
        // Defensive narrow: a malformed/tampered persisted blob may carry a
        // non-array `selected` (number/object/string). An unguarded `as` cast
        // would let `selected.includes(...)` throw and white-screen the group.
        // Validate it is an array of strings; fall back to [] otherwise
        // (11.7-REVIEW WR-03). Mission Critical: one bad row degrades, not crashes.
        const rawSelected = (state as { selected?: unknown } | undefined)?.selected;
        const selected: ReadonlyArray<string> = Array.isArray(rawSelected)
          ? rawSelected.filter((x): x is string => typeof x === "string")
          : [];
        const onSelectionChange = (next: { selected: string[] }): void => {
          stateCtx?.setWidgetState(widget.instanceId, next);
        };
        return (
          <OptionSetSurface
            key={widget.instanceId}
            instanceId={widget.instanceId}
            name={widget.name ?? "Options"}
            options={config.options}
            cardinality={config.cardinality}
            optionCategories={recipe.optionCategories}
            selected={selected}
            onSelectionChange={onSelectionChange}
            violated={violated}
            readVocab={readVocab}
          />
        );
      })}
    </div>
  );
}
RecipeOptionGroup.displayName = "RecipeOptionGroup";

// ---------------------------------------------------------------------------
// OptionSetSurface — per-widget option grid: group-bucketed auto-columns of
// selectable Pills, with the optional constraint overlay on the whole widget.
// ---------------------------------------------------------------------------

interface OptionSetSurfaceProps {
  instanceId: string;
  name: string;
  options: ReadonlyArray<RecipeOptionShape>;
  cardinality: { min?: number | undefined; max?: number | undefined };
  optionCategories: RecipeOptionCategoryShape | undefined;
  selected: ReadonlyArray<string>;
  onSelectionChange: (next: { selected: string[] }) => void;
  violated: ConstraintVerdict | undefined;
  readVocab: (slot: "container" | "subdivision" | "set" | "item") => string;
}

function OptionSetSurface({
  instanceId,
  name,
  options,
  cardinality,
  optionCategories,
  selected,
  onSelectionChange,
  violated,
  readVocab,
}: OptionSetSurfaceProps): React.ReactElement {
  // Compute the substituted explanation once per render — pure function.
  // When no violated verdict, this is unused.
  const isViolated = violated !== undefined && violated.status === "violated";
  const explanation =
    isViolated && "explanation" in violated
      ? substituteExplanation(violated.explanation, readVocab)
      : "";
  // Kind label per UI-SPEC line 400: {kind.upper} RULE.
  const kindLabel = isViolated ? `${violated.kind.toUpperCase()} RULE` : "";

  // Cardinality guards (mirror option-set/renderer.tsx:18-21). The shared
  // toggle() owns the selection transition; the Pill `disabled`/`isDisabled`
  // mirrors the renderer so an at-max unselected option is inert. For the
  // canonical {min:0, no-max} fixture atMax is ALWAYS false (Pitfall 3) — these
  // paths only fire for max-bearing recipes.
  const min = cardinality.min ?? 0;
  const max = cardinality.max;
  const atMax = max !== undefined && selected.length >= max;
  const isSingleSelectSwap = min === 1 && max === 1;

  const labelId = `${instanceId}-label`;

  // Bucket options into group columns (reuse the summary-checklist
  // bucketing shape, renderer.tsx:136-145). Key = the option's groupId DATA
  // value, or "_ungrouped" when unset. First-seen order is preserved.
  const groupOrder: string[] = [];
  const groups = new Map<string, RecipeOptionShape[]>();
  for (const opt of options) {
    const key = opt.groupId ?? "_ungrouped";
    if (!groups.has(key)) {
      groups.set(key, []);
      groupOrder.push(key);
    }
    groups.get(key)?.push(opt);
  }

  const renderOption = (opt: RecipeOptionShape): React.ReactElement => {
    if (isViolated) {
      // PRESERVED violated branch — imported from ./constraint-pill (Plan 01).
      return (
        <DisabledOptionWithTooltip
          key={opt.id}
          optionId={opt.id}
          label={opt.label}
          kindLabel={kindLabel}
          explanation={explanation}
        />
      );
    }
    const isSelected = selected.includes(opt.id);
    // at-max unselected options are inert (except the single-select swap, where
    // an unselected pill stays clickable to replace the current selection).
    const isDisabled = !isSelected && atMax && !isSingleSelectSwap;
    return (
      <Pill
        key={opt.id}
        selected={isSelected}
        disabled={isDisabled}
        data-option-id={opt.id}
        onClick={() => {
          // No-op suppression (mirrors option-set/renderer.tsx:29-37, the guard
          // 06-REVIEW WR-02 added). toggle() returns a same-membership array for
          // blocked transitions (at-min unselect, at-max add, or an already-
          // selected pill in a min===1/max===1 widget). Writing that still
          // allocates a fresh provider Map and arms the 300ms debounced PUT for
          // a click that changed nothing — so skip the write when unchanged
          // (11.7-REVIEW WR-01).
          const next = toggle(selected, opt.id, { min, max });
          const unchanged =
            next.length === selected.length && next.every((x, i) => x === selected[i]);
          if (unchanged) return;
          onSelectionChange({ selected: next });
        }}
      >
        {opt.label}
      </Pill>
    );
  };

  return (
    <section
      role="group"
      aria-labelledby={labelId}
      data-widget-instance-id={instanceId}
      className="flex flex-col gap-2"
    >
      <h3
        id={labelId}
        className="text-base font-semibold text-[var(--recipe-ink)]"
      >
        {name}
      </h3>
      {options.length === 0 ? (
        <p className="text-sm text-[var(--recipe-muted)]">No options to pick from.</p>
      ) : (
        <>
          {/* Auto-columns: one column per group bucket, no hard max
              (the cardinality omits max; do not cap the column count). */}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-4">
            {groupOrder.map((key) => {
              const bucket = groups.get(key) ?? [];
              // SHARED resolver (group-label.ts): _ungrouped → "" (empty
              // header); known id → OptionCategory.label; unknown id → raw id.
              const header = resolveGroupLabel(key, optionCategories);
              return (
                <div key={key} className="flex flex-col gap-2">
                  {header !== "" ? (
                    <h4 className="text-xs font-semibold uppercase tracking-wider leading-5 text-[var(--recipe-muted)]">
                      {header}
                    </h4>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    {bucket.map((opt) => renderOption(opt))}
                  </div>
                </div>
              );
            })}
          </div>
          {selected.length === 0 ? (
            <p className="text-xs italic text-[var(--recipe-muted)]">(none selected)</p>
          ) : null}
          {atMax && max !== undefined && !isSingleSelectSwap ? (
            <p className="text-xs text-[var(--recipe-muted)]">{max} selected — limit reached</p>
          ) : null}
        </>
      )}
    </section>
  );
}

// DisabledOptionWithTooltip lives in ./constraint-pill (Phase 11.7 D2 LIFT)
// and is imported above — its props, JSX, aria/data attributes, and the
// JSX-text-escaping security posture are preserved verbatim there. The enabled
// branch is now the interactive Pill (Plan 02) instead of the old read-only
// EnabledOption button.

// ---------------------------------------------------------------------------
// useTermReader — exposes useTerm(slot) as a closure-friendly read function
// so substituteExplanation can be called with a single (slot: VocabularySlot)
// => string argument. The hook call ordering is stable across renders since
// it always reads all four slots up-front.
// ---------------------------------------------------------------------------

function useTermReader(): (
  slot: "container" | "subdivision" | "set" | "item",
) => string {
  // Read all four slots up-front so hook-call order is stable and the
  // returned function is a plain closure (no hook calls inside its body).
  const container = useTerm("container");
  const subdivision = useTerm("subdivision");
  const set = useTerm("set");
  const item = useTerm("item");
  return (slot) => {
    switch (slot) {
      case "container":
        return container;
      case "subdivision":
        return subdivision;
      case "set":
        return set;
      case "item":
        return item;
    }
  };
}
